import { selectionsNormalizer } from '~shared/utils/dataNormalisers';

/**
 * Normalises preplay event data into inplay event data by merging default inplay properties
 * and additional event information, if provided.
 *
 * @param {number} inplayEventId - The ID of the inplay event.
 * @param {Object} [extraEventInfo] - Additional event information received from a WebSocket message.
 * @param {Object} stateEvent - The current event in the state
 * @param {string} preplayEventId - Preplay event ID to retrive event data.
 * @returns {Object} The normalised inplay event data object.
 */
export const preplayToInplayEventNormalizer = (inplayEventId, extraEventInfo, stateEvent) => {
    // We could receive a WS message with no event data, in which case we return a default object
    const normalizedEventData = {
        ...stateEvent,
        active: 1,
        event_type: 'INPLAY',
        id: String(inplayEventId),
        inplay: true,
        match_status: '',
        tradable: 0,
    };

    if (extraEventInfo) {
        const {
            active,
            name,
            tradable,
        } = extraEventInfo;

        normalizedEventData.active = active;
        normalizedEventData.name = name;
        normalizedEventData.tradable = tradable;

    }

    return normalizedEventData;
};

/**
 * Normalises preplay market event data into inplay market event data by merging default properties
 * and additional event information, if provided.
 *
 * @param {number} inplayEventId - The ID of the inplay event.
 * @param {number} preplayMarketId - The ID of the preplay market.
 * @param {Object} extraEventInfo - Additional event information received from a WebSocket message.
 * @param {Boolean} usesThreeMarketView - If the event uses three market view.
 * @returns {Object} The normalised inplay market event data object.
 */
export const preplayToInplayMarketsEventsNormalizer = (
    inplayEventId,
    preplayMarketId,
    extraEventInfo,
    usesThreeMarketView
) => {
    let normalizedMarketsEvent = [{
        id: `${inplayEventId}-${preplayMarketId}`,
        market_id: preplayMarketId,
        event_id: inplayEventId,
        active: true, // Default value, not present in WS data
        tradable: true, // Default value, not present in WS data
    }];

    if (extraEventInfo) {
        normalizedMarketsEvent = [];
        const {
            selections,
        } = extraEventInfo;
        const processedMarketIds = [];
        const defaultLineSelections = {};

        selections.forEach(selection => {
            const {
                market_id: marketId,
                id: selectionId,
             } = selection;

            if (!defaultLineSelections[marketId]) {
                defaultLineSelections[marketId] = [];
            }
            defaultLineSelections[marketId].push(selectionId);

            if (!processedMarketIds.includes(marketId)) {
                normalizedMarketsEvent.push({
                    id: `${inplayEventId}-${marketId}`,
                    market_id: String(marketId),
                    event_id: String(inplayEventId),
                    active: true, // Default value, not present in WS data
                    tradable: true, // Default value, not present in WS data
                });
                processedMarketIds.push(marketId);
            }
        });

        if (usesThreeMarketView) {
            normalizedMarketsEvent.forEach((market) => {
                const marketId = market.market_id;
                if (defaultLineSelections[marketId]) {
                    market.default_line_selections = defaultLineSelections[marketId];
                }
            });
        }
    }

    return normalizedMarketsEvent;
};

/**
 * Normalises preplay selection data and generates data pointers for inplay events.
 *
 * @param {number} inplayEventId - The ID of the inplay event.
 * @param {number} preplayMarketId - The ID of the preplay market.
 * @param {Object} extraEventInfo - Additional event information received from a WebSocket message.
 * @param {Boolean} usesThreeMarketView - If the event uses three market view.
 * @returns {Object} An object containing normalised selections and pointers.
 */
export const preplayToInplaySelectionsAndDataPointersNormalizer = (
        inplayEventId,
        preplayMarketId,
        extraEventInfo,
        usesThreeMarketView
    ) => {

    if (!extraEventInfo) {

        return {
            normalizedSelections: [],
            pointers: [{
                'event_id': inplayEventId,
                'market_id': preplayMarketId,
                // Use -1 for WS event/market updates when there are no selections.
                // Any invalid selection ID works, but -1 is the standard in our codebase.
                'selection_id': -1,
            }],
        };
    }

    const pointers = [];
    const marketSelections = {};
    const {
        id: eventId,
        selections,
    } = extraEventInfo;
    const processedMarketIds = [];
    const marketsMap = [];

    selections.forEach(selection => {
        const {
            active,
            decimal_previous_price: decimalPreviousPrice,
            decimal_price: decimalPrice,
            fractional_current_price: fractionalCurrentPrice,
            fractional_previous_price: fractionalPreviousPrice,
            id: selectionId,
            market_id: marketId,
            name,
            selection_schema: selectionSchema,
            selection_type: selectionType,
            selection_type_id: selectionTypeId,
            specialoddsvalue,
            tradable,
        } = selection;

        const selectionDetails = {
            active: Boolean(active),
            decimal_price: decimalPrice,
            fraction_price: fractionalCurrentPrice,
            name,
            old_decimal_price: decimalPreviousPrice,
            old_fraction_price: fractionalPreviousPrice,
            outcome: 'UNSETTLED', // Default value, not present in WS data
            selection_type_id: selectionTypeId,
            specialoddsvalue,
            tradable: Boolean(tradable),
            type: selectionType,
        };

        if (!marketsMap[marketId]) {
            marketsMap[marketId] = {
                selections: {},
                selection_schema: selectionSchema,
                id: marketId,
            };
        }

        marketsMap[marketId].selections[selectionId] = selectionDetails;
        marketSelections[selectionId] = selectionDetails;

        // Three market view has multiple selections per market and we only need the market ID
        if (!usesThreeMarketView || !processedMarketIds.includes(marketId)) {
            const pointer = {
                event_id: inplayEventId,
                market_id: Number(marketId),
            };

            if (!usesThreeMarketView) {
                pointer.selection_id = selectionId;
            }

            pointers.push(pointer);
            processedMarketIds.push(marketId);
        }
    });

    const { normalizedSelections } = selectionsNormalizer(marketsMap, eventId);

    return {
        normalizedSelections,
        pointers,
    };
};

/**
 * Use the preplay event data to build the inplay UI market data
 *
 * @param {number} inplayEventId - The ID of the inplay event.
 * @param {Object} preplayUiMarkets - The current state of the UI markets to be used with the new data.
 * @returns {Object} - The normalised in-play UI market data.
 */
export const preplayToInplayUiMarkets = (inplayEventId, preplayUiMarkets) => {

    const gameLineId = `gameLineMarket-${inplayEventId}`;
    const uiMarkets = {};

    if (preplayUiMarkets) {
        uiMarkets[gameLineId] = {
            ...preplayUiMarkets,
            id: gameLineId,
            event_id: inplayEventId,
        };
    }

    return uiMarkets;
};
